import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Layout, Pointer, Zap } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

// Add CSS for active tab styling
const tabTriggerStyles = `
  .tabs-trigger[data-state="active"][data-dark="true"] {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #10b981 !important;
    border: 1px solid rgba(16, 185, 129, 0.3) !important;
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.1) !important;
  }
  .tabs-trigger[data-state="active"][data-dark="false"] {
    background: rgba(16, 185, 129, 0.1) !important;
    color: #047857 !important;
    border: 1px solid rgba(16, 185, 129, 0.3) !important;
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15) !important;
  }
  .tabs-trigger:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

interface TabContent {
  badge: string;
  title: string;
  description: string;
  buttonText: string;
  buttonUrl?: string;
  imageSrc: string;
  imageAlt: string;
}

interface Tab {
  value: string;
  icon: React.ReactNode;
  label: string;
  content: TabContent;
}

interface Feature108Props {
  badge?: string;
  heading?: string;
  description?: string;
  tabs?: Tab[];
  isDark?: boolean;
}

const Feature108 = ({
  badge = "shadcnblocks.com",
  heading = "A Collection of Components Built With Shadcn & Tailwind",
  description = "Join us to build flawless web solutions.",
  isDark = false,
  tabs = [
    {
      value: "tab-1",
      icon: <Zap className="h-auto w-4 shrink-0" />,
      label: "Boost Revenue",
      content: {
        badge: "Modern Tactics",
        title: "Make your site a true standout.",
        description:
          "Discover new web trends that help you craft sleek, highly functional sites that drive traffic and convert leads into customers.",
        buttonText: "See Plans",
        imageSrc:
          "https://shadcnblocks.com/images/block/placeholder-dark-1.svg",
        imageAlt: "placeholder",
      },
    },
    {
      value: "tab-2",
      icon: <Pointer className="h-auto w-4 shrink-0" />,
      label: "Higher Engagement",
      content: {
        badge: "Expert Features",
        title: "Boost your site with top-tier design.",
        description:
          "Use stellar design to easily engage users and strengthen their loyalty. Create a seamless experience that keeps them coming back for more.",
        buttonText: "See Tools",
        imageSrc:
          "https://shadcnblocks.com/images/block/placeholder-dark-2.svg",
        imageAlt: "placeholder",
      },
    },
    {
      value: "tab-3",
      icon: <Layout className="h-auto w-4 shrink-0" />,
      label: "Stunning Layouts",
      content: {
        badge: "Elite Solutions",
        title: "Build an advanced web experience.",
        description:
          "Lift your brand with modern tech that grabs attention and drives action. Create a digital experience that stands out from the crowd.",
        buttonText: "See Options",
        imageSrc:
          "https://shadcnblocks.com/images/block/placeholder-dark-3.svg",
        imageAlt: "placeholder",
      },
    },
  ],
}: Feature108Props) => {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: tabTriggerStyles }} />
      <section className="py-16" style={{
        backgroundColor: 'transparent'
      }}>
        <div className="container mx-auto">
        <div className="flex flex-col items-center gap-4 text-center">
          {badge && <Badge variant="outline">{badge}</Badge>}
          <h1 className="max-w-2xl text-3xl font-semibold md:text-4xl" style={{
            color: isDark ? '#ffffff' : '#000000'
          }}>
            {heading}
          </h1>
          {description && <p className="lg:text-lg" style={{
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
          }}>{description}</p>}
        </div>
        <Tabs defaultValue={tabs[0].value} className="mt-8">
          <TabsList className="container flex flex-col items-center justify-center gap-4 sm:flex-row md:gap-10 bg-transparent">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="tabs-trigger flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-300"
                style={{
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  backgroundColor: 'transparent',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                }}
                data-dark={isDark}
              >
                {tab.icon} {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div 
            className="mx-auto mt-8 max-w-screen-xl rounded-2xl p-6 lg:p-16 transition-all duration-300"
            style={{
              background: isDark 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: isDark
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: isDark
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 32px rgba(31, 38, 135, 0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = isDark
                ? '0 12px 48px rgba(0, 0, 0, 0.5)'
                : '0 12px 48px rgba(31, 38, 135, 0.25)';
              e.currentTarget.style.border = isDark
                ? '1px solid rgba(255, 255, 255, 0.2)'
                : '1px solid rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = isDark
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 32px rgba(31, 38, 135, 0.15)';
              e.currentTarget.style.border = isDark
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.3)';
            }}
          >
            {tabs.map((tab) => (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className="grid place-items-center gap-20 lg:grid-cols-2 lg:gap-10"
              >
                <div className="flex flex-col gap-5">
                  <Badge 
                    variant="outline" 
                    className="w-fit" 
                    style={{
                      backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
                      border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)',
                      color: isDark ? '#10b981' : '#047857'
                    }}
                  >
                    {tab.content.badge}
                  </Badge>
                  <h3 className="text-3xl font-semibold lg:text-5xl" style={{
                    color: isDark ? '#ffffff' : '#000000'
                  }}>
                    {tab.content.title}
                  </h3>
                  <p className="lg:text-lg" style={{
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                  }}>
                    {tab.content.description}
                  </p>
                  {tab.content.buttonUrl ? (
                    <Link to={tab.content.buttonUrl}>
                      <Button 
                        className="mt-2.5 w-fit gap-2" 
                        size="lg"
                        style={{
                          background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                          border: 'none',
                          color: '#ffffff',
                          borderRadius: '12px',
                          padding: '16px 24px',
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(90deg, #059669 0%, #047857 100%)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
                          e.currentTarget.style.transform = 'translateY(0px)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        {tab.content.buttonText}
                      </Button>
                    </Link>
                  ) : (
                    <Button 
                      className="mt-2.5 w-fit gap-2" 
                      size="lg"
                      style={{
                        background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        color: '#ffffff',
                        borderRadius: '12px',
                        padding: '16px 24px',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, #059669 0%, #047857 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      {tab.content.buttonText}
                    </Button>
                  )}
                </div>
                <img
                  src={tab.content.imageSrc}
                  alt={tab.content.imageAlt}
                  className="rounded-xl"
                />
              </TabsContent>
            ))}
          </div>
        </Tabs>
        </div>
      </section>
    </>
  );
};

export { Feature108 };
